import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Check, Calendar, Flag, X, UserCheck, Clock, Loader2, ArrowRight, Camera, Paperclip, FileText, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  low: { label: 'Low', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-blue-100 text-blue-700' },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
};

const PortalTodos = () => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [completingTodo, setCompletingTodo] = useState(null);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/employee-portal/my-todos`, { headers });
      setTodos(res.data.todos || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('portal_token');
        navigate('/employee-portal/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleComplete = async (todo) => {
    try {
      await axios.patch(`${API}/api/employee-portal/my-todos/${todo.id}`, {
        completed: todo.status === 'pending_approval' ? false : !todo.completed,
      }, { headers });
      load();
    } catch (err) { console.error(err); }
  };

  // Manager/admin tasks require completion proof (note + optional photo);
  // self tasks stay a simple one-tap toggle. A task already submitted for
  // approval can be UNDONE (retracted back to active) if clicked by accident.
  const handleToggleClick = (todo) => {
    const isManagerAssigned = todo.assigned_by_type === 'manager' || todo.assigned_by_type === 'admin';
    const isCompleted = todo.status === 'completed' || (todo.completed && todo.status !== 'pending_approval');
    if (todo.status === 'pending_approval') {
      toggleComplete(todo); // retract accidental submission -> active
    } else if (isManagerAssigned && !isCompleted) {
      setCompletingTodo(todo);
    } else {
      toggleComplete(todo);
    }
  };

  const deleteTodo = async (id) => {
    try {
      await axios.delete(`${API}/api/employee-portal/my-todos/${id}`, { headers });
      setTodos(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      if (err.response?.data?.detail) {
        alert(err.response.data.detail);
      }
    }
  };

  const filtered = todos.filter(t => {
    if (filter === 'active') return t.status === 'active';
    if (filter === 'pending') return t.status === 'pending_approval';
    if (filter === 'completed') return t.status === 'completed' || t.completed;
    return true;
  });

  const activeCount = todos.filter(t => t.status === 'active' || (!t.status && !t.completed)).length;
  const pendingCount = todos.filter(t => t.status === 'pending_approval').length;
  const completedCount = todos.filter(t => t.status === 'completed' || (t.completed && t.status !== 'pending_approval')).length;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-todos">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-gray-900">My To-Do</h2>
        <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => setShowAdd(true)} data-testid="add-todo-btn">
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`text-center py-2.5 rounded-xl text-xs font-medium transition ${filter === 'all' ? 'bg-sky-100 text-sky-700 ring-1 ring-sky-200' : 'bg-gray-50 text-gray-500'}`}
          data-testid="filter-all"
        >
          All ({todos.length})
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`text-center py-2.5 rounded-xl text-xs font-medium transition ${filter === 'active' ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-200' : 'bg-gray-50 text-gray-500'}`}
          data-testid="filter-active"
        >
          Active ({activeCount})
        </button>
        {pendingCount > 0 && (
          <button
            onClick={() => setFilter('pending')}
            className={`text-center py-2.5 rounded-xl text-xs font-medium transition ${filter === 'pending' ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-200' : 'bg-gray-50 text-gray-500'}`}
            data-testid="filter-pending"
          >
            Pending ({pendingCount})
          </button>
        )}
        <button
          onClick={() => setFilter('completed')}
          className={`text-center py-2.5 rounded-xl text-xs font-medium transition ${filter === 'completed' ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200' : 'bg-gray-50 text-gray-500'}`}
          data-testid="filter-completed"
        >
          Done ({completedCount})
        </button>
      </div>

      {/* Todo List */}
      {filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <Check className="h-12 w-12 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">
              {filter === 'completed' ? 'No completed tasks' : filter === 'active' ? 'All caught up!' : filter === 'pending' ? 'No pending approvals' : 'No tasks yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((todo) => {
            const pCfg = PRIORITY_CONFIG[todo.priority] || PRIORITY_CONFIG.medium;
            const sCfg = STATUS_CONFIG[todo.status] || STATUS_CONFIG.active;
            const isOverdue = todo.due_date && todo.status === 'active' && new Date(todo.due_date + 'T23:59:59') < new Date();
            const isManagerAssigned = todo.assigned_by_type === 'manager' || todo.assigned_by_type === 'admin';
            const isPending = todo.status === 'pending_approval';
            const isCompleted = todo.status === 'completed' || (todo.completed && !isPending);

            return (
              <Card key={todo.id} className={`border-0 shadow-sm transition ${isCompleted ? 'opacity-60' : ''}`} data-testid={`todo-${todo.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => handleToggleClick(todo)}
                      className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition ${
                        isCompleted ? 'bg-emerald-500 border-emerald-500' :
                        isPending ? 'bg-amber-400 border-amber-400 hover:bg-amber-500' :
                        'border-gray-300 hover:border-sky-400'
                      }`}
                      title={isPending ? 'Undo — send back to active' : undefined}
                      data-testid={`toggle-todo-${todo.id}`}
                    >
                      {isCompleted && <Check className="h-3 w-3 text-white" />}
                      {isPending && <Clock className="h-3 w-3 text-white" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${isCompleted ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                        {todo.title}
                      </p>
                      {todo.description && (
                        <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">{todo.description}</p>
                      )}
                      {todo.reject_reason && todo.status === 'active' && (
                        <div className="mt-1.5 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5" data-testid={`reject-reason-${todo.id}`}>
                          <p className="text-[9px] font-semibold text-red-600 uppercase tracking-wide">Sent back{todo.rejected_by ? ` by ${todo.rejected_by}` : ''} — please fix</p>
                          <p className="text-[11px] text-gray-700">{todo.reject_reason}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <Badge className={`text-[9px] px-1.5 py-0 ${pCfg.color}`}>{pCfg.label}</Badge>
                        {isPending && (
                          <Badge className="text-[9px] px-1.5 py-0 bg-amber-100 text-amber-700" data-testid={`pending-badge-${todo.id}`}>
                            <Clock className="h-2.5 w-2.5 mr-0.5" /> Pending Approval
                          </Badge>
                        )}
                        {isManagerAssigned && (
                          <span className="flex items-center gap-0.5 text-[10px] text-violet-500">
                            <UserCheck className="h-2.5 w-2.5" />
                            {todo.assigned_by_type === 'admin' ? 'Admin' : todo.assigned_by_name}
                          </span>
                        )}
                        {todo.due_date && (
                          <span className={`flex items-center gap-0.5 text-[10px] ${isOverdue ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                            <Calendar className="h-2.5 w-2.5" />
                            {new Date(todo.due_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                            {isOverdue && ' (overdue)'}
                          </span>
                        )}
                      </div>
                    </div>
                    {isPending && (
                      <button
                        onClick={() => toggleComplete(todo)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-amber-600 hover:bg-amber-50 transition flex-shrink-0"
                        data-testid={`undo-todo-${todo.id}`}
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Undo
                      </button>
                    )}
                    {!isManagerAssigned && !isCompleted && (
                      <button
                        onClick={() => deleteTodo(todo.id)}
                        className="p-1 text-gray-300 hover:text-red-500 transition flex-shrink-0"
                        data-testid={`delete-todo-${todo.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Todo Dialog */}
      {showAdd && (
        <AddTodoDialog
          token={token}
          onClose={() => setShowAdd(false)}
          onSuccess={() => { setShowAdd(false); load(); }}
        />
      )}

      {/* Complete Task Dialog (proof) */}
      {completingTodo && (
        <CompleteTaskDialog
          token={token}
          todo={completingTodo}
          onClose={() => setCompletingTodo(null)}
          onDone={() => { setCompletingTodo(null); load(); }}
        />
      )}
    </div>
  );
};

/* ── Add Todo Dialog ────────────────── */
const AddTodoDialog = ({ token, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/employee-portal/my-todos`, {
        title: title.trim(),
        description,
        due_date: dueDate || null,
        priority,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="add-todo-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-gray-900">New Task</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <Label className="text-sm font-medium">Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="What needs to be done?" className="mt-1" data-testid="todo-title-input" />
          </div>
          <div>
            <Label className="text-sm font-medium">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Add details..." rows={2} className="mt-1" data-testid="todo-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Due Date</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="mt-1" data-testid="todo-due-input" />
            </div>
            <div>
              <Label className="text-sm font-medium">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1" data-testid="todo-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <Button type="submit" className="w-full py-5 bg-sky-600 hover:bg-sky-700" disabled={submitting || !title.trim()} data-testid="submit-todo-btn">
            {submitting ? 'Creating...' : <><Plus className="h-4 w-4 mr-2" /> Create Task</>}
          </Button>
        </form>
      </div>
    </div>
  );
};

/* ── Complete Task Dialog (note + photo/file proof) ────────────────── */
const CompleteTaskDialog = ({ token, todo, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setError('');
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API}/api/employee-portal/team-todos/upload-proof`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAttachments(prev => [...prev, res.data]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAtt = (id) => setAttachments(prev => prev.filter(a => a.id !== id));

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      await axios.patch(`${API}/api/employee-portal/my-todos/${todo.id}`, {
        completed: true,
        completion_note: note.trim(),
        completion_attachments: attachments,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="complete-task-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-gray-900">Complete Task</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-gray-50 rounded-xl px-3 py-2.5">
            <p className="text-sm font-medium text-gray-900">{todo.title}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">This will be sent to {todo.assigned_by_type === 'admin' ? 'Admin' : todo.assigned_by_name} for approval.</p>
          </div>
          <div>
            <Label className="text-sm font-medium">Add a note <span className="text-gray-400 font-normal">(optional)</span></Label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Done and cleaned up, all stock rotated." rows={3} className="mt-1" data-testid="complete-note-input" />
          </div>
          <div>
            <Label className="text-sm font-medium">Attach proof <span className="text-gray-400 font-normal">(optional)</span></Label>
            <div className="flex gap-2 mt-1.5">
              <Button type="button" variant="outline" className="flex-1 border-sky-200 text-sky-700 hover:bg-sky-50" onClick={() => camRef.current?.click()} disabled={uploading} data-testid="complete-camera-btn">
                <Camera className="h-4 w-4 mr-1.5" /> Photo
              </Button>
              <Button type="button" variant="outline" className="flex-1 border-gray-200 text-gray-600 hover:bg-gray-50" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="complete-attach-btn">
                <Paperclip className="h-4 w-4 mr-1.5" /> File
              </Button>
            </div>
            <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handleFiles} data-testid="complete-camera-input" />
            <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx" multiple className="hidden" onChange={handleFiles} data-testid="complete-file-input" />
            {uploading && <p className="text-[11px] text-sky-600 mt-1.5 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Uploading…</p>}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="complete-attachments">
                {attachments.map(a => (
                  <div key={a.id} className="relative group">
                    {a.kind === 'image' ? (
                      <img src={`${API}/api/employee-portal/team-todos/proof/${a.id}`} alt={a.name} className="h-16 w-16 rounded-lg object-cover border" />
                    ) : (
                      <div className="h-16 w-16 rounded-lg border bg-gray-50 flex flex-col items-center justify-center text-gray-400">
                        <FileText className="h-5 w-5" />
                        <span className="text-[8px] mt-0.5 px-1 truncate max-w-full">{a.name?.split('.').pop()}</span>
                      </div>
                    )}
                    <button onClick={() => removeAtt(a.id)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow" data-testid={`remove-att-${a.id}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <Button onClick={submit} className="w-full py-5 bg-emerald-600 hover:bg-emerald-700" disabled={submitting || uploading} data-testid="submit-complete-btn">
            {submitting ? 'Submitting…' : <><Check className="h-4 w-4 mr-2" /> Mark Complete</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PortalTodos;
